import { useCallback, useState } from 'react';

/**
 * Save-button dirty state for the video-wall content editors (LibraryEditor,
 * PlaylistEditorDialog, MenuEditor, TemplateEditor) — so Save goes
 * disabled (no changes) → enabled (dirty) → disabled (after save), matching the
 * already-correct ChangePlaylistDialog.
 *
 * `serialized` is the editor's CURRENT content as a STABLE string, built exactly
 * like the payload it persists (record id + volatile UI-only keys excluded, and
 * the SAME key order in both the baseline and the current build so the strings
 * compare cleanly).
 *
 * Call `markClean(serialized)` with that string:
 *   1. right after loading the record (in the load effect), and
 *   2. in the save success path,
 * so the baseline tracks "what is currently persisted". Pure UI; no sync impact.
 */
export default function useEditorDirty() {
  const [baseline, setBaseline] = useState(null);
  const markClean = useCallback((serialized) => setBaseline(serialized), []);
  // Until a baseline is captured (record still loading) we report not-dirty so the
  // Save button stays disabled rather than flashing enabled on an empty editor.
  const isDirty = useCallback(
    (serialized) => baseline !== null && serialized !== baseline,
    [baseline],
  );
  return { markClean, isDirty };
}
